import { useEffect, useState } from 'react';
import { TableCell, TableRow, IconButton } from '@mui/material';
import { GetApp } from '@mui/icons-material';

// Components do projeto
import KOGCollapse from '../kogCollapse/kogCollapse';
import BoxGrid from '../boxGrid/boxGrid';
import RenderTooltipMessage from '../renderTooltipMessage/renderTooltipMessage';
import LoadingComponent from '../loading/loadingComponent';

// Utils e API
import { quoteApi } from '@/main/api';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { downloadPdfFile } from '@/main/services/utilsFile';
import { FileTypeEnum } from '@/main/services/enum/FileTypeEnum';
import { getGuidScreenByRoute } from '@/main/services/utils';
import { useLocation } from 'react-router-dom';
import { ImportQuoteFileResponse } from '@/main/models/response';
import { PaginationNotFound } from '../paginationGrid/paginationTools';
import { isEmpty } from 'lodash';

interface ICollapseFilesProps {
  guidQuote: string;
  guidClaim?: string;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  reloadTrigger?: number;
}

function CollapseFiles({
  guidQuote,
  guidClaim,
  expanded,
  onToggle,
  reloadTrigger
}: ICollapseFilesProps) {
  const location = useLocation();
  const [dataGrid, setDataGrid] = useState<ImportQuoteFileResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    setDataGrid([]);

    const request = {
      guidQuote: String(guidQuote),
      guidClaim: isNullUndefined(guidClaim) ? '' : guidClaim,
      idFileType: FileTypeEnum.ImportQuote,
      guidScreen: getGuidScreenByRoute(location.pathname)
    };

    quoteApi
      .getImportQuoteFile(request)
      .then(response => {
        setDataGrid(response.data.result || []);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [guidQuote, guidClaim, location.pathname, reloadTrigger]);

  const generateNameFile = (file: ImportQuoteFileResponse) =>
    `${file.fileName}.${file.fileExtension.toLocaleLowerCase()}`;

  const generateTooltipMessageFile = (file: ImportQuoteFileResponse) => {
    const getUploaderInfo = () =>
      isNullUndefined(file.username)
        ? ''
        : `${file.username} - ${file.uploadDateFormatted}`;
    const getProcurementStatus = () =>
      isNullUndefined(file.nameProcurementStatus)
        ? ''
        : `- ${file.nameProcurementStatus}`;
    const getFileTypeLabel = () =>
      file.idFileType === FileTypeEnum.ImportQuote
        ? 'PDF'
        : `${getTranslateByKey(
            'control.changestatus'
          )} ${getProcurementStatus()}`;
    const getFormattedOrderNr = () =>
      isEmpty(file.orderNr)
        ? ''
        : `${getTranslateByKey('information.orderNr')}: ${file.orderNr}`;

    const tooltipSections = [
      generateNameFile(file),
      getUploaderInfo(),
      getFileTypeLabel(),
      getFormattedOrderNr()
    ].filter(Boolean);

    return tooltipSections.join('\n');
  };

  return (
    <KOGCollapse
      header={
        <span style={{ fontWeight: 'bold' }}>
          {getTranslateByKey('information.files')}
        </span>
      }
      isOpenCollapseinitial={false}
      forceCollapseTrigger={!expanded}
      onCustomCollapse={onToggle}
      content={
        isLoading ? (
          <LoadingComponent />
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <BoxGrid marginCustom="0">
              {dataGrid.length === 0 ? (
                <PaginationNotFound typeGrid="boxGrid" />
              ) : (
                <>
                  {dataGrid.map(file => (
                    <TableRow key={file.guidFileImport}>
                      <TableCell>
                        <RenderTooltipMessage
                          placement="top"
                          tooltipMessage={generateTooltipMessageFile(file)}
                          isWidthChildren>
                          <span style={{ fontSize: '12px' }}>
                            {generateNameFile(file)}
                          </span>
                        </RenderTooltipMessage>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            downloadPdfFile(file.file, generateNameFile(file))
                          }>
                          <GetApp fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </BoxGrid>
          </div>
        )
      }
    />
  );
}

export default CollapseFiles;
